import React, { useState, useEffect, useCallback } from "react";
import { KeyRound, Copy, Trash2, Loader2, CheckCircle2, ShieldAlert, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { getMe, listTokens, revokeToken, grantCredits } from "../lib/api";

const statusBadge = (s) =>
  s === "redeemed"
    ? "bg-emerald-100 text-emerald-700"
    : "bg-blue-100 text-blue-700";

const TokensPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isOwner, setIsOwner] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantCr, setGrantCr] = useState(1);
  const [granting, setGranting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const me = await getMe();
      setIsOwner(!!me.isOwner);
      if (me.isOwner) setTokens(await listTokens());
    } catch (e) {
      setIsOwner(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleGrant = async () => {
    if (!grantEmail.trim()) {
      toast({ title: "Enter a customer email.", variant: "destructive" });
      return;
    }
    if (Number(grantCr) < 1) {
      toast({ title: "Credits must be at least 1.", variant: "destructive" });
      return;
    }
    setGranting(true);
    try {
      const res = await grantCredits(grantEmail.trim(), grantCr);
      toast({ title: "Credits granted", description: `${res.creditsAdded} credit(s) added to ${res.email}. Balance: ${res.credits}.` });
      setGrantEmail("");
      setGrantCr(1);
      setTokens(await listTokens());
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not grant credits.", variant: "destructive" });
    } finally {
      setGranting(false);
    }
  };

  const handleCopy = async (code) => {    try {
      await navigator.clipboard.writeText(code);
      toast({ title: "Copied", description: code });
    } catch (e) {
      toast({ title: "Copy failed", description: code, variant: "destructive" });
    }
  };

  const handleRevoke = async (t) => {
    try {
      await revokeToken(t.id);
      toast({ title: "Record deleted", description: t.note || t.code });
      setTokens(await listTokens());
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not delete record.", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-gray-400" data-testid="tokens-loading"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>;
  }

  if (!isOwner) {
    return (
      <div className="max-w-lg mx-auto mt-16 bg-white border border-gray-200 rounded-lg p-8 text-center shadow-sm" data-testid="tokens-forbidden">
        <ShieldAlert className="mx-auto text-amber-500 mb-3" size={36} />
        <h2 className="text-lg font-semibold text-gray-800">Owner access only</h2>
        <p className="text-sm text-gray-500 mt-1">Only the account owner can generate and manage access tokens.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="tokens-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center"><KeyRound size={20} /></div>
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Access Tokens</h1>
          <p className="text-sm text-gray-500">Grant tax-file credits to customers who purchase. Each credit lets them create one tax file (company).</p>
        </div>
      </div>

      {/* Grant by email */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm mb-5">
        <h3 className="text-sm font-semibold text-[#1877c9] uppercase tracking-wide mb-1">Grant credits by email</h3>
        <p className="text-xs text-gray-500 mb-3">Add credits straight to a registered customer's account (no code to share). The customer must already have signed up.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Customer email</label>
            <Input type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="customer@example.com" data-testid="grant-email-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Credits (tax files)</label>
            <Input type="number" min={1} value={grantCr} onChange={(e) => setGrantCr(e.target.value)} className="w-32 text-right font-mono" data-testid="grant-credits-input" />
          </div>
          <Button onClick={handleGrant} disabled={granting} className="gap-2 bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="grant-credits-btn">
            {granting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Grant
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f7f9fc] text-gray-500 text-xs">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Credits</th>
                <th className="px-4 py-3 font-medium">Note</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Redeemed by</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-[#f9fbfe]" data-testid={`token-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-gray-800">{t.code}</td>
                  <td className="px-4 py-3 text-gray-700">{t.credits}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[220px] truncate">{t.note || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 ${statusBadge(t.status)}`}>
                      {t.status === "redeemed" ? <CheckCircle2 size={12} /> : null}
                      {t.status === "redeemed" ? "Redeemed" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{t.redeemedByEmail || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-1">
                      <button onClick={() => handleCopy(t.code)} title="Copy code" className="p-1.5 text-gray-500 hover:text-[#1877c9] hover:bg-blue-50 rounded" data-testid={`token-copy-${t.id}`}>
                        <Copy size={15} />
                      </button>
                      <button onClick={() => handleRevoke(t)} title="Delete record" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`token-revoke-${t.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No tokens yet. Generate one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TokensPage;
